import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { salesService } from "../../../modules/api_client";
import type { CreateQuotationDto, QuotationDto } from "../../../api";
import { showErrorToast, showSuccessToast } from "../../../utils/toasts";
import { AppRoutes } from "../../../common/routes/app_routes";
import { useCartStore } from "../../../hooks/stores/useCartStore";

interface UseCreateQuotationOptions {
    /**
     * Empty the cart once the quotation is created. Only for quotations built
     * from the cart itself — a one-off quotation from the compare or product
     * details page must leave the user's cart alone.
     */
    clearCartOnSuccess?: boolean;
}

export function useCreateQuotation({ clearCartOnSuccess = false }: UseCreateQuotationOptions = {}) {
    const navigate = useNavigate();
    const clearCart = useCartStore((s) => s.clearCart);
    const [ isLoading, setIsLoading ] = useState(false);

    const createQuotation = async (
        orderLines: CreateQuotationDto[ "orderLines" ],
        validityDate?: string
    ): Promise<QuotationDto | null> => {
        setIsLoading(true);
        try {
            const createQuotationDto: CreateQuotationDto = {
                orderLines,
                ...(validityDate && { validityDate }),
            };

            const response = await salesService.createQuotation(createQuotationDto);
            showSuccessToast("Quotation created successfully!");
            if (clearCartOnSuccess) clearCart();
            navigate(AppRoutes.QUOTATIONS);
            return response.data;
        } catch (error: unknown) {
            const errorMessage =
                (error as { response?: { data?: { message?: string } } })?.response
                    ?.data?.message ||
                (error as { message?: string })?.message ||
                "Failed to create quotation";
            showErrorToast(errorMessage);
            return null;
        } finally {
            setIsLoading(false);
        }
    };

    return { createQuotation, isLoading };
}

